/**
 * Lightweight visitor intelligence SDK for national-dental-support websites.
 * Tracks: page views, dwell time, scroll depth, CTA clicks, form interactions, session duration.
 * Captures: UTM params, referrer, visitor ID (localStorage + cookie).
 */

function generateId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 86400000);
  document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/;SameSite=Lax`;
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

function getOrCreateVisitorId() {
  let vid = localStorage.getItem('_vid') || getCookie('_vid');
  if (!vid) {
    vid = generateId();
    localStorage.setItem('_vid', vid);
  }
  setCookie('_vid', vid, 365);
  return vid;
}

function parseUtmParams() {
  const params = new URLSearchParams(window.location.search);
  const utm = {};
  ['source', 'medium', 'campaign', 'content', 'term'].forEach(key => {
    const val = params.get('utm_' + key);
    if (val) utm[key] = val;
  });
  if (Object.keys(utm).length > 0) {
    sessionStorage.setItem('_utm', JSON.stringify(utm));
  }
  return Object.keys(utm).length > 0 ? utm : JSON.parse(sessionStorage.getItem('_utm') || '{}');
}

export function getStoredUtm() {
  return JSON.parse(sessionStorage.getItem('_utm') || '{}');
}

export function getVisitorId() {
  return localStorage.getItem('_vid') || getCookie('_vid') || '';
}

export function getSessionId() {
  let sid = sessionStorage.getItem('_sid');
  if (!sid) {
    sid = generateId();
    sessionStorage.setItem('_sid', sid);
  }
  return sid;
}

const TRACKER_API = process.env.REACT_APP_API_BASE || 'https://nds-pipeline.orangewater-5df01598.eastus.azurecontainerapps.io/api';

export class VisitorTracker {
  constructor(endpoint = `${TRACKER_API}/tracking/event`, { site } = {}) {
    if (!site) throw new Error('VisitorTracker: site is required');
    this.endpoint = endpoint;
    this.site = site;
    this.visitorId = getOrCreateVisitorId();
    this.sessionId = getSessionId();
    this.sessionStart = Date.now();
    this.utm = parseUtmParams();
    this.scrollMilestones = new Set();
    this.destroyed = false;
    this._lastEventId = null;
    this._pageStart = null;

    this._trackPageView();
    this._initScrollTracking();
    this._initUnloadTracking();
  }

  _send(eventType, data = {}) {
    if (this.destroyed) return;
    const payload = {
      visitorId: this.visitorId,
      sessionId: this.sessionId,
      eventType,
      url: window.location.pathname,
      referrer: document.referrer,
      utm: this.utm,
      site: this.site,
      timestamp: new Date().toISOString(),
      ...data
    };

    if (eventType === 'session_end' && navigator.sendBeacon) {
      navigator.sendBeacon(this.endpoint, JSON.stringify(payload));
    } else {
      fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      }).catch(() => {});
    }
  }

  _patchDwell() {
    if (!this._lastEventId) return;
    const secs = Math.round((Date.now() - this._pageStart) / 1000);
    fetch(`${this.endpoint}/${this._lastEventId}/dwell`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dwellSeconds: secs }),
      keepalive: true,
    }).catch(() => {});
    this._lastEventId = null;
  }

  _trackPageView() {
    this._patchDwell();
    if (this.destroyed) return;
    const payload = {
      visitorId: this.visitorId,
      sessionId: this.sessionId,
      eventType: 'page_view',
      url: window.location.pathname,
      referrer: document.referrer,
      utm: this.utm,
      site: this.site,
      timestamp: new Date().toISOString(),
    };
    this._pageStart = Date.now();
    fetch(this.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    })
      .then(r => r.json())
      .then(({ _id }) => { if (_id) this._lastEventId = _id; })
      .catch(() => {});
  }

  _initScrollTracking() {
    let ticking = false;
    this._scrollHandler = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (docHeight <= 0) { ticking = false; return; }
        const pct = Math.round((scrollTop / docHeight) * 100);

        [25, 50, 75, 100].forEach(milestone => {
          if (pct >= milestone && !this.scrollMilestones.has(milestone)) {
            this.scrollMilestones.add(milestone);
            this._send('scroll_milestone', { scrollDepth: milestone });
          }
        });
        ticking = false;
      });
    };
    window.addEventListener('scroll', this._scrollHandler, { passive: true });
  }

  _initUnloadTracking() {
    this._unloadHandler = () => {
      this._patchDwell();
      this._send('session_end', {
        sessionDuration: Date.now() - this.sessionStart
      });
    };
    this._visibilityHandler = () => {
      if (document.visibilityState === 'hidden') this._unloadHandler();
    };
    document.addEventListener('visibilitychange', this._visibilityHandler);
    window.addEventListener('beforeunload', this._unloadHandler);
  }

  trackFormStart(formId) {
    this._send('form_start', { formId });
  }

  trackFormSubmit(formId) {
    this._send('form_submit', { formId });
  }

  trackCta(label, href = '') {
    this._send('cta_click', { label, href });
  }

  destroy() {
    this.destroyed = true;
    window.removeEventListener('scroll', this._scrollHandler);
    window.removeEventListener('beforeunload', this._unloadHandler);
    document.removeEventListener('visibilitychange', this._visibilityHandler);
  }
}
